import React, { useState, useEffect } from "react";
import {
  Pagination,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from "@mui/material";
import SingleCollection from "./SingleCollection";

const PaginatedCollection = ({
  items,
  loading,
  hasMore,
  fetchMore,
  type,
  subCollections,
  onExpand,
  loadingSub,
  updateSubCollectionShowStatus,
  onEditMain,
  onAddSubItem,
  onEditSubItem,
  onMoveSubItem,
  onMoveMainItem,
  setSubSearch,
  setSubStatusFilter,
  subSearchQueries,
  subStatusFilters,
  searchQuery,
}) => {
  const [page, setPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(5);

  useEffect(() => {
    const requiredItems = page * itemsPerPage;
    const startIndex = (page - 1) * itemsPerPage;
    const currentPageItems = items.slice(startIndex, startIndex + itemsPerPage);

    // Fetch more data if:
    // 1. We're not currently loading
    // 2. There are more items available (hasMore)
    // 3. The current page would require more items than we have OR
    // 4. The current page would be empty and we have more data to fetch
    const shouldFetchMore =
      !loading &&
      hasMore &&
      (requiredItems > items.length ||
        (currentPageItems.length === 0 && items.length > 0 && page > 1));

    if (shouldFetchMore) {
      fetchMore();
    }
  }, [page, items.length, hasMore, fetchMore, loading, itemsPerPage]);

  if (loading && items.length === 0) {
    return <div>Loading {type}...</div>;
  }

  // Calculate page count considering that there might be more data to fetch
  let pageCount = Math.ceil(items.length / itemsPerPage);

  // If hasMore is true and we have items that could fill the current pages,
  // add at least one more page to indicate there's more data
  if (hasMore && items.length > 0) {
    const currentPageCapacity = pageCount * itemsPerPage;
    if (items.length >= currentPageCapacity - itemsPerPage) {
      pageCount += 1;
    }
  }

  // Debug logging to help verify the fix
  if (type === "category") {
    console.log(`Pagination Debug for ${type}:`, {
      itemsLength: items.length,
      itemsPerPage,
      originalPageCount: Math.ceil(items.length / itemsPerPage),
      finalPageCount: pageCount,
      hasMore,
      currentPage: page,
    });
  }

  const paginatedItems = items.slice(
    (page - 1) * itemsPerPage,
    page * itemsPerPage
  );

  const handlePageChange = (event, value) => {
    setPage(value);
  };

  const handleItemsPerPageChange = (event) => {
    setItemsPerPage(event.target.value);
    setPage(1);
  };

  return (
    <div>
      {paginatedItems.map((item, index) => (
        <SingleCollection
          key={item.id}
          item={item}
          subItems={subCollections[item.id]}
          onExpand={() => onExpand(item.id, item.ref)}
          loadingSub={loadingSub[item.id]}
          type={type}
          updateSubCollectionShowStatus={updateSubCollectionShowStatus}
          onEditMain={onEditMain}
          onAddSubItem={onAddSubItem}
          onEditSubItem={onEditSubItem}
          onMoveSubItem={onMoveSubItem}
          onMoveMainItem={onMoveMainItem}
          setSubSearch={setSubSearch}
          setSubStatusFilter={setSubStatusFilter}
          subSearchQueries={subSearchQueries}
          subStatusFilters={subStatusFilters}
          shouldAutoOpen={index === 0 && page === 1 && searchQuery.trim()}
          searchQuery={searchQuery}
        />
      ))}
      {items.length > 0 && (
        <div
          className="colManagement_pagination_footer"
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "20px 0",
          }}
        >
          <FormControl sx={{ m: 1, minWidth: 120 }} size="small">
            <InputLabel>Items per page</InputLabel>
            <Select
              value={itemsPerPage}
              onChange={handleItemsPerPageChange}
              label="Items per page"
            >
              <MenuItem value={5}>5</MenuItem>
              <MenuItem value={10}>10</MenuItem>
              <MenuItem value={20}>20</MenuItem>
              <MenuItem value={50}>50</MenuItem>
              <MenuItem value={100}>100</MenuItem>
              <MenuItem value={200}>200</MenuItem>
            </Select>
          </FormControl>
          <Pagination
            count={pageCount}
            page={page}
            onChange={handlePageChange}
            variant="outlined"
            shape="rounded"
          />
        </div>
      )}
    </div>
  );
};

export default PaginatedCollection;
